import type {
  DelegacionProgress,
  ServicioCodigo,
} from '@/services/afipDelegacionService'

/** Services that need phase B (Computador Fiscal) after the delegation. */
export const WEBSERVICES: ServicioCodigo[] = ['wsccomu', 'wsmtxca']

/** Time after the dispatch within which the server has to create the `job` row. */
export const PLAZO_ARRANQUE_MS = 2 * 60_000

export type EstadoDelJob =
  | { tipo: 'esperando' }
  | { tipo: 'sin_arranque' }
  | { tipo: 'en_curso' }
  | { tipo: 'completado' }
  | { tipo: 'fallido'; mensaje: string | null }

/**
 * State of an auto-delegation run, read from its `job` row. Only that row ends the run.
 * Without the row, the run is `esperando` until `PLAZO_ARRANQUE_MS` after `desde`, and
 * `sin_arranque` after it.
 */
export const estadoDelJob = (
  filas: DelegacionProgress[],
  jobId: string,
  desde: number,
  ahora: number,
): EstadoDelJob => {
  const marcador = filas.find((f) => f.job_id === jobId && f.step === 'job')
  if (!marcador) {
    return ahora - desde >= PLAZO_ARRANQUE_MS ? { tipo: 'sin_arranque' } : { tipo: 'esperando' }
  }
  if (marcador.status === 'completado') return { tipo: 'completado' }
  if (marcador.status === 'fallido') return { tipo: 'fallido', mensaje: marcador.message }
  return { tipo: 'en_curso' }
}

/**
 * Result of a run per service. A webservice is ready when its `computador_fiscal_*` step is
 * `completado`. Any other service is ready when its `delegando_*` step is `completado`. The
 * message is the one of the `job` row.
 */
export const resumenDelJob = (
  filas: DelegacionProgress[],
  jobId: string,
  servicios: ServicioCodigo[],
): { listos: number; total: number; mensaje: string | null } => {
  const delJob = filas.filter((f) => f.job_id === jobId)
  const completado = (step: string) =>
    delJob.some((f) => f.step === step && f.status === 'completado')
  const listos = servicios.filter((codigo) =>
    completado(WEBSERVICES.includes(codigo) ? `computador_fiscal_${codigo}` : `delegando_${codigo}`),
  ).length
  const mensaje = delJob.find((f) => f.step === 'job')?.message ?? null
  return { listos, total: servicios.length, mensaje }
}
